import { StethoscopeIcon, Popup, Paragraph } from '@quickmd-llc/design-system'
import React from 'react'
import { getVisitPopUpTitle } from 'utils/getVisitPopUpTitle'
import { TabSection } from './components/TabSection'
import { VisitRightHeader } from './components/VisitRightHeader'
import { VisitElapsedTimer } from './components/VisitElapsedTimer'
import { VisitNoteView } from './components/VisitNoteView'
import { VisitICD10CodesView } from './components/icd-codes/VisitICD10CodesView'
import { VisitOrdersView } from './components/orders/VisitOrdersView'
import { VisitInstructionsView } from './components/VisitInstructionsView'
import { useRequestedPharmacy } from './components/useRequestedPharmacy'
import { PatientCard } from './components/patient-views/PatientCard'
import { ProgramVisitBadge } from 'components/program-visit-badge/ProgramVisitBadge'
import { readVisitCoverage } from '@quickmd-llc/frontend-utils'
import { InPersonVisitPlaceholder } from './components/patient-views/InPersonVisitPlaceholder'
import { VisitPatientInformation } from './components/patient-views/VisitPatientInformation'
import { VisitResponsesPanel } from './components/patient-views/VisitResponsesPanel'
import { VisitAlertsPanel } from './components/patient-views/VisitAlertsPanel'
import { DailyVisit } from 'pages/WaitingRoom/daily/DailyVisit'
import { type WaitingRoomV2VisitListItemDoctorApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { useVisitPopUpState } from './useVisitPopUpState'
import { VisitPopUpModals } from './VisitPopUpModals'

type Props = {
  onClose: () => void
  upcomingVisits?: WaitingRoomV2VisitListItemDoctorApiOutContract[]
  onDeferred?: (visitId: string) => void
  onCompleted?: (visitId: string) => void
}

export const VisitPopUp = ({ onClose, upcomingVisits, onDeferred, onCompleted }: Props) => {
  // Both panels cover the same column, so one piece of state keeps them mutually exclusive.
  const [openPanel, setOpenPanel] = React.useState<'responses' | 'alerts' | null>(null)
  // Stable identities: VisitPatientInformation is memo'd, and this subtree re-renders alongside a
  // live video call, so inline arrows here would defeat that memo on every Daily event.
  const openResponses = React.useCallback(() => setOpenPanel('responses'), [])
  const openAlerts = React.useCallback(() => setOpenPanel('alerts'), [])
  const closePanel = React.useCallback(() => setOpenPanel(null), [])
  const {
    patientWithVisitInfo,
    patient,
    friendlyVisitDateTime,

    visitNoteRef,
    visitInstructionsRef,

    isClosePopupOpen,
    showCompleteConfirmation,
    setShowCompleteConfirmation,
    showDeferConfirmation,
    setShowDeferConfirmation,
    requestDefer,
    isUnresolvedOpen,
    showAuthorizeOrderReminder,
    setShowAuthorizeOrderReminder,
    setIsLoading,
    icdCodesError,
    setIcdCodesError,
    visitDetails,

    handleClose,
    handleDefer,
    handleDismissClosePopup,
    handleConfirmClose,
    handleSubmitUnresolved,
    handleComplete,
    checkCanCompleteVisit,
    handleUnresolved,
    handleDismissUnresolved,
    getOrders,
    isStillThereOpen,
    handleStillThereContinue,
    claimTimeoutMinutes,
  } = useVisitPopUpState({ onClose, onDeferred, onCompleted })
  const requestedPharmacy = useRequestedPharmacy(
    patientWithVisitInfo.VisitMethod,
    visitDetails?.PatientPreference,
    patient,
  )

  return (
    <div>
      <Popup
        lockOpen
        isOpen
        styles={{
          wrapperClassName: '!w-full !h-screen !rounded-none',
          containerClassName: '!h-screen !max-h-screen !w-full !max-w-full !p-0 visit-popup-fullscreen',
          parentClassName: '!p-0',
        }}
        onClose={onClose}
        body={
          <div className='relative box-border flex h-screen w-full min-w-[1157px] flex-col overflow-hidden bg-white'>
            <div className='box-border flex h-max w-full items-center justify-between border border-border-medium bg-white pl-sp-2'>
              <div className='flex h-max items-center gap-sp-1'>
                <TabSection
                  section={getVisitPopUpTitle(patientWithVisitInfo.VisitMethod)}
                  icon={<StethoscopeIcon color='text-text-dark' />}
                />
                <Paragraph size='sm' className='text-text-light'>
                  {friendlyVisitDateTime}
                </Paragraph>
                <VisitElapsedTimer />
                {/*
                  Reads off the `/api/v2/visits/{id}` query `useVisitPopUpState` already has in
                  flight, so the chip costs a field read rather than a request.
                */}
                <ProgramVisitBadge coverage={readVisitCoverage(visitDetails)} />
              </div>
              <div className='flex items-center pr-sp-1'>
                <VisitRightHeader
                  onComplete={checkCanCompleteVisit}
                  onUnresolved={handleUnresolved}
                  onClose={handleClose}
                  onDefer={requestDefer}
                  upcomingVisits={upcomingVisits}
                />
              </div>
            </div>

            <div className='flex min-h-0 w-full flex-1 overflow-hidden'>
              <div className='relative flex w-1/3 flex-col overflow-hidden border-r border-border-medium bg-deep-navy-5'>
                {patientWithVisitInfo.VisitMethod === 'inperson' ?
                  <InPersonVisitPlaceholder />
                : patientWithVisitInfo.VisitMethod === 'asynchronous' ?
                  null
                : <DailyVisit />}
                <div className='flex flex-col'>
                  <div className='bg-white'>
                    <PatientCard
                      patient={patient ?? {}}
                      avatarSize='xs'
                      dataTypes={['demo', 'mrn', 'dob', 'phone', 'allergies']}
                      hasBadgeRow
                      showChart
                      includePreferredName
                    />
                  </div>
                  <VisitPatientInformation onOpenResponses={openResponses} onOpenAlerts={openAlerts} />
                </div>
                <VisitResponsesPanel isOpen={openPanel === 'responses'} onClose={closePanel} />
                <VisitAlertsPanel isOpen={openPanel === 'alerts'} onClose={closePanel} />
              </div>
              <div className='flex min-h-0 w-2/3 flex-col'>
                <div className='flex h-full min-h-0'>
                  <div className='box-border flex h-full min-h-0 w-full flex-col gap-[1px] bg-border-medium'>
                    <div className='min-h-0 flex-[2]'>
                      <VisitNoteView ref={visitNoteRef} />
                    </div>
                    <div className='min-h-0 flex-1'>
                      <VisitInstructionsView ref={visitInstructionsRef} />
                    </div>
                  </div>
                  <div className='relative box-border flex h-full w-[320px] min-w-[320px] shrink-0 flex-col overflow-y-auto border border-border-medium'>
                    <VisitICD10CodesView hasError={icdCodesError} clearError={() => setIcdCodesError(false)} />
                    {patientWithVisitInfo.VisitMethod !== 'inperson' && (
                      <VisitOrdersView setIsLoading={setIsLoading} requestedPharmacy={requestedPharmacy} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      />

      <VisitPopUpModals
        patientId={patientWithVisitInfo?.PatientId ?? undefined}
        visitId={patientWithVisitInfo?.VisitId ?? undefined}
        patient={patient}
        visitDetails={visitDetails}
        isUnresolvedOpen={isUnresolvedOpen}
        onDismissUnresolved={handleDismissUnresolved}
        onSubmitUnresolved={handleSubmitUnresolved}
        isClosePopupOpen={isClosePopupOpen}
        onDismissClosePopup={handleDismissClosePopup}
        onConfirmClose={handleConfirmClose}
        showCompleteConfirmation={showCompleteConfirmation}
        onCompleteConfirmationClose={() => setShowCompleteConfirmation(false)}
        onHandleComplete={handleComplete}
        showDeferConfirmation={showDeferConfirmation}
        onDeferConfirmationClose={() => setShowDeferConfirmation(false)}
        onHandleDefer={() => {
          setShowDeferConfirmation(false)
          handleDefer()
        }}
        showAuthorizeOrderReminder={showAuthorizeOrderReminder}
        onAuthorizeOrderClose={() => setShowAuthorizeOrderReminder(null)}
        isStillThereOpen={isStillThereOpen}
        onStillThereDefer={handleDefer}
        onStillThereContinue={handleStillThereContinue}
        stillThereTimeoutMinutes={claimTimeoutMinutes}
        getOrders={getOrders}
      />
    </div>
  )
}
